//services/auth/authService.ts
import { useApi } from '@/composables/useApi'
import { signInWithCustomToken, signOut, onAuthStateChanged } from 'firebase/auth'
import type { User } from 'firebase/auth'
import type { Administrator } from '@farmfree/common/types/administrator/administrator'
import type { Company } from '@farmfree/common/types/company/company'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'
import { getFirebaseAuth } from '../firebaseService'
import { createDocumentService } from '../common/documentService'
import type { CustomerProfile } from '@farmfree/common/types/customer-profile/customerProfile'

export function createAuthService() {
  const api = useApi() // ✅ 여기서 axios 인스턴스 생성
  const auth = getFirebaseAuth()

  return {
    async sendSms(phoneNumber: string): Promise<{ passwordType: 'random' | 'user-set' }> {
      try {
        const response = await api.post('/api/auth/send-sms', { phoneNumber }) // api 인스턴스 사용
        console.log('SMS sent successfully:', response.data)
        return {
          passwordType: response.data?.data?.passwordType || 'random',
        }
      } catch (error: any) {
        console.error('Failed to send SMS:', error.response?.data || error.message)
        throw new Error(error.response?.data?.message || 'Failed to send SMS')
      }
    },
    async verifySms(
      phoneNumber: string,
      code: string
    ): Promise<{ user: User | null; customToken: string }> {
      try {
        const response = await api.post('/api/auth/verify-sms', { phoneNumber, code })

        console.log('response:', response)
        console.log('status:', response.status)
        if (!response.data?.data?.customToken) {
          throw new Error('서버 응답에 customToken이 없습니다')
        }

        const { customToken } = response.data.data

        console.log('SMS 인증 성공 ', customToken)
        const userCredential = await signInWithCustomToken(auth, customToken)
        return { user: userCredential.user, customToken }
      } catch (error: any) {
        console.error('Failed to verify SMS:', error.response?.data || error.message)
        throw new Error(error.response?.data?.message || 'Failed to verify SMS')
      }
    },

    // 회원가입 시 user와 company를 함께 생성해야 하므로
    // auth 흐름 안에서 addUserAndCompany로 처리함
    // 향후 복잡해질 경우 registrationService로 분리 고려
    async createCompany(uid: string, company: Company): Promise<ApiResponse> {
      try {
        const response = await api.post('/api/auth/createCompany', {
          uid,
          company,
        })
        console.log('Company created via Firebase Functions:', response.data)
        return response.data as ApiResponse
      } catch (error: any) {
        console.error(
          'Failed to create company via Firebase Functions:',
          error.response?.data || error.message
        )
        throw new Error(error.response?.data?.message || 'Failed to create company')
      }
    },

    async addKakaoChannelToCompany(
      companyId: string,
      kakaoChannelId: string,
      senderKey: string,
      phoneNumber: string
    ): Promise<ApiResponse> {
      try {
        const response = await api.post('/api/auth/addKakaoChannelToCompany', {
          companyId,
          kakaoChannelId,
          senderKey,
          phoneNumber,
        })
        console.log('Kakao channel added to company via Firebase Functions:', response.data)
        return response.data as ApiResponse
      } catch (error: any) {
        console.error(
          'Failed to add kakao channel to company via Firebase Functions:',
          error.response?.data || error.message
        )
        throw new Error(error.response?.data?.message || 'Failed to add kakao channel to company')
      }
    },

    async addAdministratorAndCompany(
      uid: string,
      securedPhoneSub1: string,
      searchField: string[],
      company: Company
    ): Promise<ApiResponse> {
      try {
        // Firebase Functions 엔드포인트 호출
        // administrator는 이미 생성 되어 있으니 업데이트를 해야 맞는거지만
        // administrator 전체 전보를 주고 set을 하고 있음..문제 될 것 같지는 않음.
        const response = await api.post('/api/auth/addAdministratorAndCompany', {
          uid,
          securedPhoneSub1,
          searchField,
          company,
        })
        console.log('User and company added via Firebase Functions:', response.data)
        return response.data as ApiResponse
      } catch (error: any) {
        console.error(
          'Failed to add user and company via Firebase Functions:',
          error.response?.data || error.message
        )
        throw new Error(error.response?.data?.message || 'Failed to add user and company')
      }
    },

    async changePassword(
      uid: string,
      currentPassword: string | null,
      newPassword: string
    ): Promise<void> {
      try {
        const response = await api.post('/api/auth/change-password', {
          uid,
          currentPassword,
          newPassword,
        })
        console.log('비밀번호 변경 성공:', response.data)
      } catch (error: any) {
        console.error('비밀번호 변경 실패:', error.response?.data || error.message)
        throw new Error(error.response?.data?.message || '비밀번호 변경에 실패했습니다.')
      }
    },

    async logout(): Promise<void> {
      try {
        await signOut(auth)
        console.log('User logged out successfully')
      } catch (error: any) {
        console.error('Failed to log out:', error.message)
        throw new Error('Failed to log out')
      }
    },
    onAuthStateChange(callback: (user: User | null) => void): void {
      onAuthStateChanged(auth, callback)
    },

    // async tokenLogin(token: string): Promise<ApiResponse<{ user: any }>> {
    //   try {
    //     const response = await api.post('/api/auth/token-login', { token });
    //     console.log('✅ token-login 응답:', response.data);
    //     return response.data;
    //   } catch (error: any) {
    //     console.error('❌ token-login 실패:', error.response?.data || error.message);
    //     throw new Error(error.response?.data?.message || '자동 로그인 실패');
    //   }
    // },
  }
}

export interface AuthService {
  // login(email: string, password: string): Promise<User | null>;
  sendSms(phoneNumber: string): Promise<{ passwordType: 'random' | 'user-set' }>
  verifySms(phoneNumber: string, code: string): Promise<{ user: User | null; customToken: string }>
  createCompany(uid: string, company: Company): Promise<ApiResponse>
  addAdministratorAndCompany(
    uid: string,
    securedPhoneSub1: string,
    searchField: string[],
    company: Company
  ): Promise<ApiResponse>
  changePassword(uid: string, currentPassword: string | null, newPassword: string): Promise<void>
  logout(): Promise<void>
  onAuthStateChange(callback: (user: User | null) => void): void
  // tokenLogin(token: string): Promise<ApiResponse<{ user: any }>>;
}
